import classNames from 'classnames'
import { useMemo } from 'react'
import { Color } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { Icon } from '@opallabs/lapidary'
import { decorateServiceName } from '@opallabs/wormhole'
import * as styles from './styles'

export type ChannelIconProps = {
  /**
   * Any valid CSS color can be provided as an override to the
   * channel's default color
   */
  colorOverride?: string
  /** Size of the Icon container element */
  iconSize?: number
  /** Source URL for a channel's custom image, if it has one */
  imageSrc?: string
  /** The channel's color */
  channelColor?: string
  /** Name of the channel */
  channelName: string
  /** Name of the preset channel icon if one was selected */
  channelIconName?: string
  /** System name of the channel */
  channelSystemName: string
  /** Indicates that the current user does not have access to this channel */
  privateForUser: boolean
}

export const ChannelIcon: React.FC<ChannelIconProps> = ({
  colorOverride,
  iconSize,
  imageSrc,
  channelColor,
  channelName,
  channelIconName,
  channelSystemName,
  privateForUser,
}) => {
  const theme = useTheme()

  const iconDimensions = useMemo(
    () => ({
      width: iconSize,
      height: iconSize,
    }),
    [iconSize]
  )

  if (imageSrc) {
    return (
      <span
        className={classNames('satellite-channel-icon')}
        css={styles.frame}
        style={iconDimensions}
      >
        <img alt={channelName} css={styles.image} src={imageSrc} />
      </span>
    )
  }

  const iconName = channelIconName ?? channelSystemName
  const decoratedServiceName = decorateServiceName(iconName, null, true, true)

  return (
    <span className={classNames('satellite-channel-icon')}>
      <Icon
        ariaLabel={channelName}
        color={
          privateForUser
            ? Color[theme].Text.Gray
            : (colorOverride ?? channelColor)
        }
        glyph={decoratedServiceName}
        height={iconSize}
        useHypernova={true}
        width={iconSize}
      />
    </span>
  )
}
